/**
 * Event Type Add/Edit form.
 *
 * Add: full form. Edit: name + URI read-only, a read-only header (number, dataSchemaUri,
 * timestamps), rows rebuilt from dataSchema (read-only JSON fallback when they can't be shown),
 * inactive entity/action shown "(inactive)" and blocking save, and an active→inactive confirm.
 *
 * Pickers: entity/action from the API (active, non-archived only); publisher/subscriber from
 * ReMa resource instances (component category; "Not found in ReMa" for unknown/placeholder URIs).
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { ArrowLeft, Plus, Trash2, Webhook } from 'lucide-react'
import { EVENT_WORKLOAD_INSTANCES } from '@/forms/events/eventConstants'
import type { EventType, ReferenceFieldRow } from '@/types/events'
import {
  addEventTypeSchema, editEventTypeSchema, rowsToJsonSchema, jsonSchemaToRows, deriveUri, hasBlockedWord,
} from '@/forms/events/shared/eventTypeSchema'

const log = AppLogger.getLogger(import.meta)

export interface EventTypeFormProps {
  mode: 'add' | 'edit'
  initial?: EventType
  onSuccess: () => void
  onCancel: () => void
}

const toggle = (arr: string[], v: string) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v])

export function EventTypeForm({ mode, initial, onSuccess, onCancel }: EventTypeFormProps) {
  const { toast } = useToast()
  const isEdit = mode === 'edit'

  // Option sources
  const { data: entitiesData } = useApiQuery('getEventEntityTypes', {}, {})
  const { data: actionsData } = useApiQuery('getEventActions', {}, {})
  const { data: instancesData } = useApiQuery('getResourceInstances', {}, {})

  const activeOptions = (rows: any[], numKey: string, nameKey: string) =>
    rows.filter((r) => !r.archiveDate && r.status === 'active').map((r) => ({ value: String(r[numKey]), label: r[nameKey] }))

  const entityTypeRows = ((entitiesData as any)?.content?.entityTypes ?? []) as any[]
  const entityOptions = activeOptions(entityTypeRows, 'entityTypeNumber', 'entityTypeName')
  const actionOptions = activeOptions((actionsData as any)?.content?.actions ?? [], 'actionNumber', 'actionName')

  const apiInstances = ((instancesData as any)?.content?.resourceInstances ?? []) as any[]
  // Fall back to seed instances when ReMa returns nothing (no backend), so the pickers work.
  const sourceInstances = apiInstances.length > 0 ? apiInstances : EVENT_WORKLOAD_INSTANCES
  const workloadInstances = sourceInstances
    .map((i) => ({ uri: i.instanceUri as string, name: i.resourceInstanceName as string, category: i.resourceCategory as string }))
  const components = workloadInstances.filter((i) => i.category === 'component')
  const workloadOptions = (components.length > 0 ? components : workloadInstances)

  // Field state
  const [label, setLabel] = React.useState(initial?.eventTypeLabel ?? '')
  const [name, setName] = React.useState(initial?.eventTypeName ?? '')
  const [description, setDescription] = React.useState(initial?.eventTypeDescription ?? '')
  const [uri, setUri] = React.useState(initial?.eventTypeUri ?? '')
  const [entityNum, setEntityNum] = React.useState(initial ? String(initial.entityTypeNumber) : '')
  const [actionNum, setActionNum] = React.useState(initial ? String(initial.actionNumber) : '')
  const [publisherUri, setPublisherUri] = React.useState(initial?.publisherUri ?? '')
  const [subscriberUris, setSubscriberUris] = React.useState<string[]>(initial?.subscriberUris ?? [])
  const [status, setStatus] = React.useState<string>(initial?.status ?? 'inactive')

  // Topic is derived from the selected entity type (backend assigns it) — read-only here.
  const derivedTopic = entityTypeRows.find((e) => String(e.entityTypeNumber) === entityNum)?.topic ?? initial?.topic ?? ''

  // Reference fields: rows, or a read-only JSON fallback (edit only)
  const initialRows = isEdit && initial?.dataSchema ? jsonSchemaToRows(initial.dataSchema) : []
  const [rows, setRows] = React.useState<ReferenceFieldRow[]>(initialRows ?? [])
  const readOnlyJson = isEdit && initialRows === null ? JSON.stringify(initial?.dataSchema, null, 2) : null

  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [confirmInactive, setConfirmInactive] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)

  // Per-row validation for the data fields, shown inline (and live once there's input).
  const fieldNameError = (name: string): string | null => {
    const n = name.trim()
    if (!n) return submitted ? 'Field name is required' : null
    if (/\s/.test(name)) return 'No spaces — use camelCase, e.g. farmNumber'
    if (!/^[a-z][a-zA-Z0-9]*$/.test(n)) return 'Use camelCase, starting with a lowercase letter, e.g. farmNumber'
    if (hasBlockedWord(n)) return "Field names can't contain credentials, codes or keys"
    return null
  }
  const duplicateNames = React.useMemo(() => {
    const seen = new Set<string>(); const dups = new Set<string>()
    rows.forEach((r) => { const n = r.name.trim(); if (!n) return; if (seen.has(n)) dups.add(n); else seen.add(n) })
    return dups
  }, [rows])

  const upsert = useApiMutation('upsertEventType', 'POST', { invalidates: ['getEventTypes'] })

  // URI is always auto-generated from the name (read-only); edit mode keeps the original.
  React.useEffect(() => {
    if (!isEdit) setUri(name ? deriveUri(name) : '')
  }, [name, isEdit])

  // If the event's entity/action is now inactive, show it but block save.
  const entityInactive = isEdit && !!initial && !entityOptions.some((o) => o.value === String(initial.entityTypeNumber))
  const actionInactive = isEdit && !!initial && !actionOptions.some((o) => o.value === String(initial.actionNumber))
  const entitySelectOptions = entityInactive && initial ? [...entityOptions, { value: String(initial.entityTypeNumber), label: `${initial.entityTypeName ?? initial.entityTypeNumber} (inactive)` }] : entityOptions
  const actionSelectOptions = actionInactive && initial ? [...actionOptions, { value: String(initial.actionNumber), label: `${initial.actionName ?? initial.actionNumber} (inactive)` }] : actionOptions
  const staleBlocked = (entityInactive && entityNum === String(initial?.entityTypeNumber)) || (actionInactive && actionNum === String(initial?.actionNumber))

  const addRow = () => setRows((r) => [...r, { name: '', type: 'string', required: true, description: '' }])
  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i))
  const updateRow = (i: number, patch: Partial<ReferenceFieldRow>) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))

  const doSubmit = async () => {
    setErrors({})
    const common = {
      eventTypeLabel: label, eventTypeDescription: description,
      entityTypeNumber: entityNum, actionNumber: actionNum,
      publisherUri, subscriberUris, status, referenceFields: rows,
    }
    const parsed = isEdit
      ? editEventTypeSchema.safeParse({ ...common, eventTypeNumber: initial!.eventTypeNumber })
      : addEventTypeSchema.safeParse({ ...common, eventTypeName: name, eventTypeUri: uri })

    if (!parsed.success) {
      const next: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form')
        if (!next[key]) next[key] = issue.message
      }
      setErrors(next)
      toast({ title: 'Please fix the highlighted fields', description: 'Some required fields are missing or invalid.', type: 'error' })
      return
    }
    if (staleBlocked) {
      toast({ title: 'Inactive classification', description: 'Pick an active entity type and action before saving.', type: 'error' })
      return
    }

    // Build dataSchema: from rows, or keep the original schema when it was shown read-only.
    const dataSchema = readOnlyJson ? initial?.dataSchema : rowsToJsonSchema(rows)
    const { referenceFields, ...rest } = parsed.data as Record<string, unknown>
    void referenceFields
    // eventTypeName and eventTypeUri are create-only (so the edit schema drops them), but the
    // upsert body requires them on every call — send the originals (name/uri hold them in both modes).
    const payload = {
      ...rest,
      entityTypeNumber: Number(entityNum),
      actionNumber: Number(actionNum),
      eventTypeName: name,
      eventTypeUri: uri,
      dataSchema,
    }

    try {
      await upsert.mutateAsync(payload)
      toast({ title: isEdit ? 'Event type updated' : 'Event type created', type: 'success' })
      onSuccess()
    } catch (err) {
      const msg = (err as Error)?.message ?? ''
      // 409 → field error on whichever field the response names.
      if (/name/i.test(msg) && /exist|duplicate|409/i.test(msg)) setErrors((e) => ({ ...e, eventTypeName: 'An event type with this name already exists' }))
      else if (/uri/i.test(msg) && /exist|duplicate|409/i.test(msg)) setErrors((e) => ({ ...e, eventTypeUri: 'An event type with this URI already exists' }))
      else toast({ title: isEdit ? 'Could not update event type' : 'Could not create event type', description: msg || 'Please try again.', type: 'error' })
      log.error('upsertEventType failed', err)
    }
  }

  const handleSubmit = () => {
    setSubmitted(true)
    // Confirm when flipping an existing active event to inactive.
    if (isEdit && initial?.status === 'active' && status === 'inactive') {
      setConfirmInactive(true)
      return
    }
    doSubmit()
  }

  const err = (k: string) => errors[k] && <p className="text-sm text-destructive">{errors[k]}</p>

  return (
    <div className="space-y-4">
      <Button variant="ghost" className="gap-2" onClick={onCancel}><ArrowLeft className="h-4 w-4" />Back to Event Types</Button>

      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Webhook className="h-5 w-5 text-primary" /></div>
            <div>
              <CardTitle className="text-lg">{isEdit ? 'Edit Event Type' : 'Add Event Type'}</CardTitle>
              <CardDescription>Enter the event type details below.</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="space-y-8 pt-6">
          {isEdit && initial && (
            <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/20 p-4 text-sm md:grid-cols-4">
              <div><p className="text-xs text-muted-foreground">Number</p><p className="font-mono">{initial.eventTypeNumber}</p></div>
              <div className="truncate"><p className="text-xs text-muted-foreground">Data schema URI</p><p className="font-mono truncate">{initial.dataSchemaUri ?? '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">Created</p><p>{initial.createdAt ? new Date(initial.createdAt).toLocaleDateString() : '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">Updated</p><p>{initial.updatedAt ? new Date(initial.updatedAt).toLocaleDateString() : '—'}</p></div>
            </div>
          )}

          {/* Identity */}
          <div className="space-y-4">
            <h3 className="border-b pb-2 text-base font-semibold">Identity</h3>
            <div className="space-y-2"><Label>Label <span className="text-destructive">*</span></Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Farm Added" />{err('eventTypeLabel')}</div>
            <div className="space-y-2"><Label>Name <span className="text-destructive">*</span></Label><Input value={name} onChange={(e) => setName(e.target.value)} disabled={isEdit} className="font-mono" placeholder="farmAdded" /><p className="text-xs text-muted-foreground">camelCase, unique{isEdit ? ' — locked in edit' : ''}.</p>{err('eventTypeName')}</div>
            <div className="space-y-2"><Label>Description (sent as CloudEvent subject) <span className="text-destructive">*</span></Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} />{err('eventTypeDescription')}</div>
            <div className="space-y-2"><Label>Event Type URI</Label><Input value={uri} readOnly disabled className="font-mono" placeholder="Generated from the name" /><p className="text-xs text-muted-foreground">Generated automatically from the name — not editable.</p>{err('eventTypeUri')}</div>
          </div>

          {/* Classification */}
          <div className="space-y-4">
            <h3 className="border-b pb-2 text-base font-semibold">Classification</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Entity Type <span className="text-destructive">*</span></Label>
                <Select value={entityNum} onValueChange={setEntityNum}><SelectTrigger><SelectValue placeholder="Select entity type" /></SelectTrigger>
                  <SelectContent>{entitySelectOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>{err('entityTypeNumber')}</div>
              <div className="space-y-2"><Label>Action <span className="text-destructive">*</span></Label>
                <Select value={actionNum} onValueChange={setActionNum}><SelectTrigger><SelectValue placeholder="Select action" /></SelectTrigger>
                  <SelectContent>{actionSelectOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>{err('actionNumber')}</div>
            </div>
          </div>

          {/* Routing */}
          <div className="space-y-4">
            <h3 className="border-b pb-2 text-base font-semibold">Routing</h3>
            <div className="space-y-2"><Label>Topic</Label>
              <Input value={derivedTopic} readOnly disabled placeholder="Set by the selected entity type" />
              <p className="text-xs text-muted-foreground">Derived from the entity type — not editable.</p></div>
            <div className="space-y-2"><Label>Publisher <span className="text-destructive">*</span></Label>
              <Select value={publisherUri} onValueChange={setPublisherUri}><SelectTrigger><SelectValue placeholder="Select publisher" /></SelectTrigger>
                <SelectContent>
                  {publisherUri && !workloadOptions.some((o) => o.uri === publisherUri) && <SelectItem value={publisherUri}>{publisherUri} (Not found in ReMa)</SelectItem>}
                  {workloadOptions.map((o) => <SelectItem key={o.uri} value={o.uri}>{o.name}</SelectItem>)}
                </SelectContent></Select>{err('publisherUri')}</div>
            <div className="space-y-2"><Label>Subscribers</Label>
              <div className="flex flex-col gap-2 rounded-md border p-3 max-h-40 overflow-y-auto">
                {workloadOptions.length === 0 ? <p className="text-sm text-muted-foreground">No workloads available</p> : workloadOptions.map((o) => (
                  <label key={o.uri} className="flex items-center gap-2 text-sm"><Checkbox checked={subscriberUris.includes(o.uri)} onCheckedChange={() => setSubscriberUris((a) => toggle(a, o.uri))} />{o.name}</label>
                ))}
                {subscriberUris.filter((u) => !workloadOptions.some((o) => o.uri === u)).map((u) => (
                  <label key={u} className="flex items-center gap-2 text-sm"><Checkbox checked onCheckedChange={() => setSubscriberUris((a) => toggle(a, u))} /><span className="text-amber-600">{u} (Not found in ReMa)</span></label>
                ))}
              </div>{err('subscriberUris')}</div>
            <div className="space-y-2 max-w-xs"><Label>Status</Label>
              <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select></div>
          </div>

          {/* Data fields */}
          <div className="space-y-3">
            <h3 className="border-b pb-2 text-base font-semibold">Data fields</h3>
            {readOnlyJson ? (
              <div className="space-y-2"><p className="text-sm text-muted-foreground">This schema was edited outside this screen and is shown read-only.</p>
                <pre className="rounded-md border bg-muted/30 p-3 text-xs overflow-x-auto">{readOnlyJson}</pre></div>
            ) : (
              <>
                {rows.length > 0 && (
                  <div className="grid grid-cols-12 gap-2 px-1 text-xs font-medium text-muted-foreground">
                    <div className="col-span-3">Field name</div><div className="col-span-2">Type</div>
                    <div className="col-span-2">Required</div><div className="col-span-4">Description</div><div className="col-span-1" />
                  </div>
                )}
                {rows.map((row, i) => {
                  const nameErr = fieldNameError(row.name)
                  const isDup = duplicateNames.has(row.name.trim())
                  const rowErr = nameErr ?? (isDup ? 'Another field already uses this name' : null)
                  return (
                    <div key={i} className="space-y-1">
                      <div className="grid grid-cols-12 items-start gap-2">
                        <div className="col-span-3"><Input value={row.name} onChange={(e) => updateRow(i, { name: e.target.value })} placeholder="fieldName" className={`font-mono ${rowErr ? 'border-destructive focus-visible:ring-destructive' : ''}`} /></div>
                        <div className="col-span-2"><Select value={row.type} onValueChange={(v) => updateRow(i, { type: v as ReferenceFieldRow['type'] })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['string', 'number', 'boolean', 'object', 'array'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                        <label className="col-span-2 flex items-center gap-2 pt-2 text-sm"><Checkbox checked={row.required} onCheckedChange={(c) => updateRow(i, { required: c === true })} />required</label>
                        <div className="col-span-4"><Input value={row.description ?? ''} onChange={(e) => updateRow(i, { description: e.target.value })} placeholder="What this field holds (optional)" /></div>
                        <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => removeRow(i)}><Trash2 className="h-4 w-4" /></Button></div>
                      </div>
                      {rowErr && <p className="text-sm text-destructive">{rowErr}</p>}
                    </div>
                  )
                })}
                <Button variant="outline" size="sm" className="gap-2" onClick={addRow}><Plus className="h-4 w-4" />Add field</Button>
                <p className="text-xs text-muted-foreground">Optional. Field names must be camelCase with no spaces (e.g. <span className="font-mono">farmNumber</span>). Leave empty if the event carries no data.</p>
                {rows.length === 0 && <p className="text-sm text-amber-600">This event carries no data.</p>}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={upsert.isPending}>{upsert.isPending ? 'Saving...' : isEdit ? 'Save' : 'Create'}</Button>
      </div>

      <Dialog open={confirmInactive} onOpenChange={setConfirmInactive}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Deactivate this event?</DialogTitle>
            <DialogDescription>Publishing services will reject this event once they reload the catalog. Continue?</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmInactive(false)}>Cancel</Button><Button onClick={() => { setConfirmInactive(false); doSubmit() }}>Continue</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isEdit && (entityInactive || actionInactive) && (
        <Badge variant="secondary" className="fixed bottom-4 right-4">Pick active entity/action to save</Badge>
      )}
    </div>
  )
}
